import { useState } from "react"
import { iconForContentType } from "../../lib/iconForContentType"
import { AttachmentModel } from "../reduxToolkit/attachmentsSlice"
import { FilePreviewModal } from "./filePreview"
import { useAppSelector } from "../hooks/reduxToolkit"
import { humanize } from "../../lib/util/string"
import Toast from 'react-hot-toast'
import { useAsyncAction } from "../hooks/useAsyncAction"

import './attachment.scss'
import { useClient } from "../providers/supabase"
import { downloadBlob } from "../../lib/util/downloadBlob"

interface AttachmentProps {
  attachment: AttachmentModel
}

export function Attachment({
  attachment,
}: AttachmentProps) {
  const [showPreview, setShowPreview] = useState(false)

  // Get blob from Redux instead of using useLoadBlobs
  const blob = useAppSelector((s) => s.blobs.blobs.find((b) => b.key === attachment.blob_key))

  const client = useClient()
  const [doDownloadState, doDownload] = useAsyncAction(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!blob) {
      return
    }
    
    try {
      await downloadBlob(blob, { client: client })
    } catch (e: any) {
      Toast.error('Error downloading file: ' + e.message)
    }
  })

  const icon = iconForContentType(blob?.content_type)
  
  let inner: React.ReactNode
  if (doDownloadState.loading) {
    inner = <div className="spinner-border spinner-border-sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  } else if (doDownloadState.error) {
    inner = <i className="material-icons">error</i>
  } else {
    inner = <i className="material-icons">file_download</i>
  }

  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  return <a className={`attachment`} onClick={() => setShowPreview(true)}>
    <i className='material-icons-outlined'>{icon}</i>
    <span className="attachment__name">
      {!blob ? 'Unable to load file' : blob.file_name}
    </span>

    <span className="attachment__purpose ms-auto d-none d-md-inline">
      {attachment.purpose ? humanize(attachment.purpose) : ''}
    </span>

    <button className="btn btn-link" onClick={doDownload}>
      {inner}
    </button>

    <FilePreviewModal show={!!showPreview} fileOrBlob={blob} onClose={() => setShowPreview(false)} />
  </a>
}
